import { IconArrow } from '@/assets/icons/IconArrow';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { SignInMethod } from './SnapshotUser.types';
import styles from './snapshotpopover.module.scss';

const SIGN_IN_METHOD_OPTIONS: Array<{
    description?: string;
    label: string;
    value: SignInMethod;
}> = [
    {
        value: 'text',
        label: 'Text me a verification code',
        description: 'Send to ***-***-0838',
    },
    {
        value: 'email',
        label: 'Email me a verification code',
        description: 'Send to l******@gmail.com',
    },
    {
        value: 'password',
        label: 'Sign in with my password',
    },
];

interface SnapshotUserChooseSignInMethodProps {
    appdownloadItems: DownloadButtonProps[];
    onBack: () => void;
    onSelectMethod: (method: SignInMethod) => void;
    selectedMethod: SignInMethod;
}

export function SnapshotUserChooseSignInMethod({
    appdownloadItems,
    onBack,
    onSelectMethod,
    selectedMethod,
}: SnapshotUserChooseSignInMethodProps): React.JSX.Element {
    return (
        <div className={styles.snapshotUserFlow}>
            <div className={styles.snapshotSubscreenTopBar}>
                <button
                    aria-label="Go back"
                    className={styles.snapshotBackButton}
                    onClick={onBack}
                    type="button"
                >
                    <IconArrow />
                </button>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotSubscreenMain}>
                <div className={styles.snapshotSubscreenSection}>
                    <Text tag="h3" className={styles.snapshotAnonymousTitle}>
                        How would you like to sign in?
                    </Text>

                    <div
                        aria-label="Sign in methods"
                        className={styles.snapshotMethodOptions}
                        role="radiogroup"
                    >
                        {SIGN_IN_METHOD_OPTIONS.map((option) => {
                            const isSelected = selectedMethod === option.value;

                            return (
                                <button
                                    aria-checked={isSelected}
                                    className={styles.snapshotMethodOption}
                                    key={option.value}
                                    onClick={() => onSelectMethod(option.value)}
                                    role="radio"
                                    type="button"
                                >
                                    <span className={styles.snapshotMethodRadio}>
                                        <span
                                            className={styles.snapshotMethodRadioOuter}
                                            data-selected={isSelected}
                                        >
                                            {isSelected && (
                                                <span className={styles.snapshotMethodRadioInner} />
                                            )}
                                        </span>
                                    </span>

                                    <span className={styles.snapshotMethodCopy}>
                                        <Text tag="span" className={styles.snapshotMethodLabel}>
                                            {option.label}
                                        </Text>
                                        {option.description && (
                                            <Text tag="span" className={styles.snapshotMethodDescription}>
                                                {option.description}
                                            </Text>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {selectedMethod === 'text' && (
                        <Text tag="p" className={styles.snapshotMethodLegal}>
                            By tapping &quot;Continue&quot; you agree to receive a one-time
                            automated message at the phone number selected for the purpose of
                            receiving a verification code. Message and data rates may apply.
                        </Text>
                    )}
                </div>

                <div className={styles.snapshotMethodActions}>
                    <Button
                        ariaLabel="Continue"
                        fitting="fill-container"
                        size="small"
                        type="button"
                        variant="primary"
                    >
                        Continue
                    </Button>
                </div>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotAnonymousFooter}>
                <Text tag="h4" className={styles.snapshotAppHeading}>
                    Get the SiriusXM app
                </Text>
                <AppDownloadButtons
                    items={appdownloadItems}
                    className={styles.snapshotDownloadButtons}
                    appleClassName={styles.snapshotStoreBadge}
                    androidClassName={styles.snapshotStoreBadge}
                />
            </div>
        </div>
    );
}
